import { getSectionDescription } from "@/constants/sections";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import { SectionDashboard } from "@/components/dashboard/SectionDashboard";
import { useStore } from "@/store";

/**
 * Section dashboard page component.
 * Fetches section dashboard data and renders SectionDashboard component.
 */
export default function SectionDashboardPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const sectionId = (params as { section_id?: string }).section_id ?? null;
  const companyId = (params as { company_id?: string }).company_id ?? null;
  const getSectionForCompany = useStore((state) => state.getSectionForCompany);

  // Redirect to /app if companyId or sectionId is missing
  useEffect(() => {
    if (!companyId || !sectionId) {
      void navigate({ to: "/app", replace: true });
    }
  }, [companyId, sectionId, navigate]);

  // Find the company and section from the store using shared selector
  const { sectionName, sectionDescription } = useMemo(() => {
    const defaultResult = { sectionName: "Dashboard", sectionDescription: null as string | null };

    if (!companyId || !sectionId) {
      return defaultResult;
    }

    const result = getSectionForCompany(companyId, sectionId);
    if (!result) {
      return defaultResult;
    }

    const { section } = result;
    return {
      sectionName: section.name,
      sectionDescription: section.description ?? getSectionDescription(sectionId),
    };
  }, [companyId, sectionId, getSectionForCompany]);

  // Don't render if companyId or sectionId is missing (redirect will happen)
  if (!companyId || !sectionId) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <SectionDashboard
        sectionId={sectionId}
        sectionName={sectionName}
        description={sectionDescription}
        companyId={companyId}
      />
    </div>
  );
}
